import { inject, Injectable, InjectionToken } from '@angular/core';
import { LoadChildrenCallback, Route, Router, Routes } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ICON } from '@shared/constants/icons.constant';
import { __ } from '@shared/functions';
import { hasUnsupportedScope, SearchConnector, SearchScope, SearchResultItem } from '@shared/global-search';
import { Icon } from '@shared/types/icon.type';
import { firstValueFrom, from, isObservable, map, Observable, of } from 'rxjs';

/** Maximum results a single section contributes — the palette shows sections, not routes. */
const MAX_RESULTS = 8;

/** Per-route global-search metadata, read from `Route.data.search`. */
export interface RouteSearchMeta {
  /** Force this route in or out of search, overriding the active strategy. Inherited by children. */
  include?: boolean;
  /**
   * Force lazy `loadChildren` under this route to be resolved (or skipped) for
   * indexing, overriding the global `RESOLVE_LAZY_SEARCH_ROUTES` default. Inherited
   * by children, so a single flag on a feature root covers its whole subtree.
   */
  resolveChildren?: boolean;
  /** Palette title override; falls back to `route.title`, then `data.title`, then the full path. */
  title?: string;
  /** Extra terms matched against the query, beyond the path and data. */
  keywords?: string[];
}

/** A route paired with its fully-resolved path and effective search metadata. */
interface FlatRoute {
  route: Route;
  fullPath: string;
  meta: RouteSearchMeta | undefined;
}

/** State that cascades from a parent route to its descendants while flattening. */
interface InheritedSearchState {
  include?: boolean;
  resolveChildren?: boolean;
}

/**
 * Decides whether a navigable route is exposed to global search. Receives the
 * route and its effective (inheritance-applied) metadata.
 */
export type RouteSearchStrategy = (route: Route, meta: RouteSearchMeta | undefined) => boolean;

/** Opt-out policy: every route is searchable unless `data.search.include === false`. */
export const includeByDefaultStrategy: RouteSearchStrategy = (_route, meta) => meta?.include !== false;

/** Opt-in policy: only routes with `data.search.include === true` are searchable. */
export const optInSearchStrategy: RouteSearchStrategy = (_route, meta) => meta?.include === true;

/**
 * Strategy deciding, per route, whether it participates in global search. Override
 * via `{ provide: ROUTE_SEARCH_STRATEGY, useValue: optInSearchStrategy }` to switch
 * policies, or supply a custom predicate for app-specific rules.
 */
export const ROUTE_SEARCH_STRATEGY = new InjectionToken<RouteSearchStrategy>('ROUTE_SEARCH_STRATEGY', {
  providedIn: 'root',
  factory: () => includeByDefaultStrategy,
});

/** Global default for resolving lazy `loadChildren` routes when building the index. */
export const RESOLVE_LAZY_SEARCH_ROUTES = new InjectionToken<boolean>('RESOLVE_LAZY_SEARCH_ROUTES', {
  providedIn: 'root',
  factory: () => false,
});

/**
 * Global-search connector for the application's registered routes, so a user can
 * jump straight to a page instead of hunting through the nav bar.
 *
 * Builds a flattened route index from `Router.config` once on first use — resolving
 * lazy `loadChildren` boundaries so deep routes are searchable — then filters that
 * cached index per query. The `ROUTE_SEARCH_STRATEGY` decides which routes are
 * eligible; `RESOLVE_LAZY_SEARCH_ROUTES` (and per-parent `data.search.resolveChildren`)
 * control lazy resolution.
 *
 * Register globally via `provideSearchConnectors(RoutesSearchConnector)` or pass
 * per call through `openGlobalSearch(injector, { connectors: [...] })`.
 */
@Injectable({ providedIn: 'root' })
export class RoutesSearchConnector implements SearchConnector {
  // -----------------------------------------------------------------------------------------------------
  // @ DEPENDENCIES
  // -----------------------------------------------------------------------------------------------------

  private readonly router = inject(Router);

  private readonly isSearchable = inject(ROUTE_SEARCH_STRATEGY);

  private readonly resolveLazyRoutes = inject(RESOLVE_LAZY_SEARCH_ROUTES);

  private readonly t = inject(TranslateService);

  // -----------------------------------------------------------------------------------------------------
  // @ INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  readonly id = 'routes';

  readonly label = 'Seiten';

  readonly order = 10;

  readonly icon: Icon = ICON.paperPlaneRight;

  /** Flattened route index, resolved once (including lazy children) and reused across searches. */
  private flatRoutesCache?: Promise<FlatRoute[]>;

  /** Per-loader cache of resolved lazy children, so each `loadChildren` runs at most once. */
  private readonly lazyChildrenCache = new Map<LoadChildrenCallback, Promise<Routes | undefined>>();

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Searches the registered routes for ones matching the query. The route index is
   * built (and lazy boundaries resolved) once on first use, then reused; matching,
   * scope narrowing, dedupe, and capping run per query against the cache.
   *
   * @param query - The trimmed search term.
   * @param scope - The active scope.
   * @returns {Observable<SearchResultItem[]>} The matching routes as palette results, capped at MAX_RESULTS.
   */
  search(query: string, scope: SearchScope): Observable<SearchResultItem[]> {
    if (__.IsNullOrUndefinedOrEmpty(query)) {
      return of([]);
    }

    // Pages are scope-independent — inside a scoped palette they would only be noise
    if (hasUnsupportedScope(scope, [])) {
      return of([]);
    }

    return from(this.getFlatRoutes()).pipe(map((flats) => this.toResults(flats, query, scope)));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Returns the flattened route index, building it once on first use. Lazy
   * `loadChildren` routes are resolved during the build, so the returned list
   * includes routes behind lazy boundaries.
   *
   * @returns {Promise<FlatRoute[]>} The cached, fully-resolved flat route list.
   */
  private getFlatRoutes(): Promise<FlatRoute[]> {
    this.flatRoutesCache ??= this.flattenRoutes(this.router.config);

    return this.flatRoutesCache;
  }

  /**
   * Filters the flattened routes against the query and scope, dedupes colliding
   * paths, caps the count, and maps survivors to palette rows.
   *
   * @param flats - The flattened route index.
   * @param query - The trimmed search term.
   * @param scope - The active scope.
   * @returns {SearchResultItem[]} The palette rows for this query.
   */
  private toResults(flats: FlatRoute[], query: string, scope: SearchScope): SearchResultItem[] {
    const matches = flats
      .filter((flat) => this.isNavigable(flat))
      .filter((flat) => this.isSearchable(flat.route, flat.meta))
      .filter((flat) => this.matchesQuery(flat, query, scope));

    return this.dedupeByPath(matches)
      .slice(0, MAX_RESULTS)
      .map((flat) => this.toResultItem(flat));
  }

  /**
   * Recursively flattens the route tree into routes paired with their full path and
   * effective search metadata, resolving lazy `loadChildren` boundaries along the
   * way. The `include` and `resolveChildren` flags inherit from parent to child.
   *
   * @param routes - The routes to flatten.
   * @param parentPath - The accumulated path of the parent route.
   * @param inherited - Flags cascaded from the parent route.
   * @returns {Promise<FlatRoute[]>} Every route in the subtree with its resolved path and metadata.
   */
  private async flattenRoutes(routes: Route[], parentPath = '', inherited: InheritedSearchState = {}): Promise<FlatRoute[]> {
    const subtrees = await Promise.all(routes.map((route) => this.flattenRoute(route, parentPath, inherited)));

    return subtrees.flat();
  }

  /**
   * Flattens a single route and its resolved children.
   *
   * @param route - The route to flatten.
   * @param parentPath - The accumulated path of the parent route.
   * @param inherited - Flags cascaded from the parent route.
   * @returns {Promise<FlatRoute[]>} This route followed by its flattened descendants.
   */
  private async flattenRoute(route: Route, parentPath: string, inherited: InheritedSearchState): Promise<FlatRoute[]> {
    const fullPath = this.joinPaths(parentPath, route.path);
    const ownMeta = this.readSearchMeta(route);

    const effectiveInclude = ownMeta?.include ?? inherited.include;
    const effectiveResolve = ownMeta?.resolveChildren ?? inherited.resolveChildren;

    const self: FlatRoute = { route, fullPath, meta: this.withInclude(ownMeta, effectiveInclude) };

    const children = await this.resolveChildren(route, effectiveResolve);

    if (__.IsNullOrUndefinedOrEmpty(children)) {
      return [self];
    }

    const childState: InheritedSearchState = { include: effectiveInclude, resolveChildren: effectiveResolve };

    return [self, ...(await this.flattenRoutes(children, fullPath, childState))];
  }

  private joinPaths(parentPath: string, childPath: string | undefined): string {
    return [parentPath, childPath]
      .filter((segment) => !__.IsNullOrUndefinedOrEmpty(segment))
      .join('/');
  }

  private readSearchMeta(route: Route): RouteSearchMeta | undefined {
    return route.data?.['search'] as RouteSearchMeta | undefined;
  }

  private withInclude(meta: RouteSearchMeta | undefined, include: boolean | undefined): RouteSearchMeta | undefined {
    if (__.IsNullOrUndefined(meta) && __.IsNullOrUndefined(include)) {
      return undefined;
    }

    return { ...meta, include };
  }

  /**
   * Resolves a route's children, loading lazy `loadChildren` definitions when
   * enabled — by the global default or a per-parent `resolveChildren` override — so
   * deep routes behind lazy boundaries become searchable.
   *
   * @param route - The route whose children to resolve.
   * @param resolveLazy - The effective per-parent lazy-resolution flag, if any.
   * @returns {Promise<Routes | undefined>} The child routes, or undefined for a leaf.
   */
  private async resolveChildren(route: Route, resolveLazy: boolean | undefined): Promise<Routes | undefined> {
    if (!__.IsNullOrUndefinedOrEmpty(route.children)) {
      return route.children;
    }

    if (__.IsNullOrUndefined(route.loadChildren)) {
      return undefined;
    }

    const shouldResolve = resolveLazy ?? this.resolveLazyRoutes;

    if (__.IsNullOrUndefinedOrFalse(shouldResolve)) {
      return undefined;
    }

    return this.loadLazyChildren(route.loadChildren);
  }

  private loadLazyChildren(loader: LoadChildrenCallback): Promise<Routes | undefined> {
    let cached = this.lazyChildrenCache.get(loader);

    if (__.IsNullOrUndefined(cached)) {
      cached = this.invokeLoader(loader);
      this.lazyChildrenCache.set(loader, cached);
    }

    return cached;
  }

  /**
   * Runs a lazy loader and normalizes its result to a `Routes` array. Loaders that
   * resolve to something other than routes (e.g. a legacy NgModule) yield undefined.
   *
   * @param loader - The route's `loadChildren` callback.
   * @returns {Promise<Routes | undefined>} The loaded child routes, or undefined.
   */
  private async invokeLoader(loader: LoadChildrenCallback): Promise<Routes | undefined> {
    try {
      const loaded = await this.toPromise(loader());

      return Array.isArray(loaded) ? (loaded as Routes) : undefined;
    } catch {
      return undefined;
    }
  }

  private toPromise<T>(value: T | Promise<T> | Observable<T>): Promise<T> {
    if (value instanceof Promise) {
      return value;
    }

    if (isObservable(value)) {
      return firstValueFrom(value);
    }

    return Promise.resolve(value);
  }

  /**
   * A route is navigable when it renders content of its own and is neither the
   * catch-all wildcard nor a pure redirect. Pathless grouping/wrapper routes that
   * only host children are excluded here — their children carry the real paths.
   *
   * @param flat - The flattened route.
   * @returns {boolean} True when the route can be navigated to directly.
   */
  private isNavigable(flat: FlatRoute): boolean {
    return (
      flat.route.path !== '**' &&
      __.IsNullOrUndefinedOrEmpty(flat.route.redirectTo) &&
      this.isRenderable(flat.route)
    );
  }

  private isRenderable(route: Route): boolean {
    return (
      !__.IsNullOrUndefined(route.component) ||
      !__.IsNullOrUndefined(route.loadComponent) ||
      !__.IsNullOrUndefined(route.loadChildren)
    );
  }

  /**
   * Determines whether a route matches the query, and — when a scope context is
   * present — whether it also matches that context.
   *
   * @param flat - The flattened route.
   * @param query - The trimmed search term.
   * @param scope - The active scope.
   * @returns {boolean} True when the route should appear in the results.
   */
  private matchesQuery(flat: FlatRoute, query: string, scope: SearchScope): boolean {
    const queryLower = query.toLowerCase();

    const matchesText =
      flat.fullPath.toLowerCase().includes(queryLower) ||
      this.resolveTitle(flat).toLowerCase().includes(queryLower) ||
      this.matchesKeywords(flat.meta, queryLower) ||
      this.matchesRouteData(flat.route, queryLower);

    if (!matchesText) {
      return false;
    }

    // TODO: Omit for now, has no use here.
    if (!__.IsNullOrUndefined(scope.context)) {
      // return this.matchesScope(flat.route, scope.context);
    }

    return true;
  }

  private matchesKeywords(meta: RouteSearchMeta | undefined, queryLower: string): boolean {
    const keywords = meta?.keywords;

    if (__.IsNullOrUndefinedOrEmpty(keywords)) {
      return false;
    }

    return keywords.some((keyword) => keyword.toLowerCase().includes(queryLower));
  }

  private matchesRouteData(route: Route, queryLower: string): boolean {
    if (__.IsNullOrUndefined(route.data)) {
      return false;
    }

    return JSON.stringify(route.data).toLowerCase().includes(queryLower);
  }

  private matchesScope(route: Route, context: Record<string, unknown>): boolean {
    if (__.IsNullOrUndefined(route.data)) {
      return false;
    }

    return Object.entries(context).some(([key, value]) => route.data?.[key] === value);
  }

  /**
   * Collapses routes that resolve to the same full path, keeping the most specific
   * destination — so an empty-path base-layout wrapper yields to the dashboard it
   * contains, and a lazy wrapper yields to the component page at the same path.
   *
   * @param routes - The candidate routes.
   * @returns {FlatRoute[]} One route per distinct full path.
   */
  private dedupeByPath(routes: FlatRoute[]): FlatRoute[] {
    const byPath = new Map<string, FlatRoute>();

    for (const flat of routes) {
      const existing = byPath.get(flat.fullPath);

      if (__.IsNullOrUndefined(existing) || this.isMoreSpecific(flat, existing)) {
        byPath.set(flat.fullPath, flat);
      }
    }

    return [...byPath.values()];
  }

  private isMoreSpecific(candidate: FlatRoute, current: FlatRoute): boolean {
    return this.specificity(candidate) > this.specificity(current);
  }

  /**
   * Ranks how directly a route renders a page at its path: a component leaf beats a
   * lazy wrapper, which beats a container that only hosts children.
   *
   * @param flat - The flattened route.
   * @returns {number} Higher means a more specific navigation target.
   */
  private specificity(flat: FlatRoute): number {
    if (!__.IsNullOrUndefinedOrEmpty(flat.route.children)) {
      return 0;
    }

    if (!__.IsNullOrUndefined(flat.route.component) || !__.IsNullOrUndefined(flat.route.loadComponent)) {
      return 2;
    }

    return 1;
  }

  /**
   * Resolves the display title for a route, preferring its search-meta title, then
   * the route's own `title`, then `data.title`, then falling back to the full path
   * (or `/` for the root).
   *
   * @param flat - The flattened route.
   * @returns {string} The human-facing title.
   */
  private resolveTitle(flat: FlatRoute): string {
    const metaTitle = flat.meta?.title;

    if (!__.IsNullOrUndefinedOrEmpty(metaTitle)) {
      return metaTitle;
    }

    const routeTitle = flat.route.title;

    if (typeof routeTitle === 'string' && !__.IsNullOrUndefinedOrEmpty(routeTitle)) {
      return routeTitle;
    }

    const dataTitle = flat.route.data?.['title'];

    if (!__.IsNullOrUndefinedOrEmpty(dataTitle)) {
      return String(dataTitle);
    }

    return __.IsNullOrUndefinedOrEmpty(flat.fullPath) ? '/' : flat.fullPath;
  }

  /**
   * Maps a flattened route to a palette result with its navigate action.
   *
   * @param flat - The flattened route.
   * @returns {SearchResultItem} The palette row.
   */
  private toResultItem(flat: FlatRoute): SearchResultItem {
    return {
      id: flat.fullPath,
      title: this.t.instant(this.resolveTitle(flat)),
      subtitle: `/${flat.fullPath}`,
      icon: this.icon,
      payload: flat.route,
      actions: [
        {
          id: 'open',
          label: 'Öffnen',
          icon: ICON.caretRight,
          execute: () => {
            this.router.navigateByUrl(`/${flat.fullPath}`);
          },
        },
      ],
    };
  }
}
